/**
 * Cheetah LZ17 dual-zone packing sheet for London Smoke & Cure.
 *
 * OUTSIDE (sticky label 160×105mm): shipping / rider face — QR, address, windows,
 * notes, chilled handoff banner, signatures, then prominent company name below the signing
 * space. NO quantities or prices.
 *
 * INSIDE (below perforation, rotated -90°): detail face — qty, products, unit/line prices,
 * invoice address, order total. NO QR, NO delivery window / chilled handoff banner.
 */
import logoSrc from '../images/logo.png';
import { resolvePackingTempCategoryKey } from './packingTempCheckCategory';
import { renderOrderQrSvgMap } from './orderQrSvg';
import { formatOrderQrPayload } from './orderQrPayload';
import { getOrderDeliveryDate, getOrderDispatchDate } from './orderDateUtils';
import { toOrderDeskCheckoutDate } from '../services/dateService';
import unifiedProductService from '../services/unifiedProductService';
import {
  PACKING_SLIP_DEFAULT_LOGO_URL,
  PACKING_SLIP_ORDERDESK_DEFAULTS,
  PACKING_SLIP_STORE_NAME,
  formatPackingSlipDateTime,
  formatPackingSlipLineName,
} from './packingSlipHtml';
import { formatPackWeightKg } from '../services/packingLineWeightService';
import {
  CHEETAH_LZ17_PAPER,
  CHEETAH_LZ17_CONTENT_SPLIT,
  getCheetahStubViewportMm,
  resolveCheetahLabelFit,
} from './cheetahLz17Geometry';
import { enrichOrdersWithAccountDeliveryFields } from './enrichOrdersWithAccountDeliveryFields';
import { applyCompanyCheckoutLine, resolvePackingSlipCompanyName } from './packingSlipCompany';
import { buildPackingSlipLineModel } from './packingSlipModel';
import { paginatePackingSlipLines } from './packingSlipPagination';

const sanitize = (str) => String(str ?? '').replace(/\uFFFD/g, '').trim();

const getOrderNumber = (order) => {
  const raw = String(
    order?.source_id ||
      order?.sourceId ||
      order?.orderNumber ||
      order?.order_number ||
      order?.orderId ||
      order?.id ||
      '',
  ).trim();
  return raw.replace(/^#/, '');
};

const getOrderItems = (order) => {
  if (Array.isArray(order?.originalItems) && order.originalItems.length) return order.originalItems;
  if (Array.isArray(order?.order_items) && order.order_items.length) return order.order_items;
  if (Array.isArray(order?.items) && order.items.length) return order.items;
  return [];
};

const getItemSku = (item) => {
  const raw =
    item?.sku ??
    item?.code ??
    item?.Code ??
    item?.product_code ??
    item?.productCode ??
    item?.product_sku ??
    item?.productSku ??
    '';
  return String(raw || '').trim().toUpperCase();
};

const getItemUnitPrice = (it) => {
  const raw =
    it?.price ?? it?.unit_price ?? it?.unitPrice ?? it?.price_per_unit ?? it?.Price ?? null;
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
};

const formatGbp = (n) => {
  if (n == null || !Number.isFinite(n)) return '';
  return `£${n.toFixed(2)}`;
};

const getShippingMethod = (order) =>
  sanitize(order?.shipping_method || order?.shippingMethod || order?.shipping?.method || '');

const buildShipTo = (order) => {
  const name = [
    order?.shipping?.first_name || order?.shippingFirstName || '',
    order?.shipping?.last_name || order?.shippingLastName || '',
  ]
    .join(' ')
    .trim();
  // Same company resolution as Standard slips / Printing Toolkit summary (account → OD shipping/customer).
  const company = sanitize(resolvePackingSlipCompanyName(order));
  const lines = [
    order?.shipping?.address1 || order?.shippingStreet || order?.shippingAddress?.line1,
    order?.shipping?.address2 || order?.shippingAddress?.line2,
    order?.shipping?.city || order?.shippingCity || order?.shippingAddress?.city,
    order?.shipping?.postal_code ||
      order?.shippingPostalCode ||
      order?.shipping?.postCode ||
      order?.shippingAddress?.postCode,
  ]
    .map(sanitize)
    .filter(Boolean);
  return { name: sanitize(name), company, lines };
};

const buildInvoiceTo = (order) => {
  const name = [
    order?.customer?.first_name || order?.customerFirstName || '',
    order?.customer?.last_name || order?.customerLastName || '',
  ]
    .join(' ')
    .trim();
  const company = sanitize(resolvePackingSlipCompanyName(order));
  const lines = [
    order?.customer?.address1,
    order?.customer?.address2,
    order?.customer?.city,
    order?.customer?.postal_code || order?.customer?.postalCode,
  ]
    .map(sanitize)
    .filter(Boolean);
  return { name: sanitize(name), company, lines };
};

const formatDeliveryWindow = (order) => {
  const start = sanitize(
    order?.windowStart || order?.accountWindowStart || order?._accountDelivery?.windowStart || '',
  );
  const end = sanitize(
    order?.windowEnd || order?.accountWindowEnd || order?._accountDelivery?.windowEnd || '',
  );
  if (start && end) return `${start} – ${end}`;
  if (start) return `From ${start}`;
  if (end) return `Until ${end}`;
  return '';
};

const collectDeliveryNotes = (order) => {
  const parts = [];
  const accountNote = sanitize(
    order?.accountPackingSlipNote ||
      order?.packingSlipNote ||
      order?.packing_slip_note ||
      order?._accountDelivery?.packingSlipNote ||
      order?.customer?.packingSlipNote ||
      '',
  );
  if (accountNote) parts.push(accountNote);

  const checkoutNotes = sanitize(
    order?.checkout_data?.Notes || order?.checkoutData?.Notes || order?.notes || '',
  );
  if (checkoutNotes && checkoutNotes !== accountNote) parts.push(checkoutNotes);

  if (Array.isArray(order?.order_notes)) {
    order.order_notes.forEach((n) => {
      const t = sanitize(typeof n === 'string' ? n : n?.content);
      if (t && !parts.includes(t)) parts.push(t);
    });
  }
  return parts;
};

/** True when the order has salmon or sausage lines (keep cold). */
export function orderNeedsRefrigerateSoon(order) {
  return getOrderItems(order).some((it) => {
    const sku = getItemSku(it);
    const category = it?.category || it?.Category || it?.name || '';
    return !!resolvePackingTempCategoryKey(category, sku);
  });
}

/**
 * Bacon / cured charcuterie (not fresh sausage, not salmon).
 * Used for the shelf-stable sunlight warning when the order is only these.
 */
export function itemLooksBaconOrCharcuterie(item) {
  if (!item) return false;
  const sku = getItemSku(item);
  const category = String(item?.category || item?.Category || '').toLowerCase();
  const name = String(item?.name || item?.Name || '').toLowerCase();
  const blob = `${category} ${name} ${sku}`.toLowerCase();

  // Fresh sausage / salmon take priority elsewhere — exclude here
  if (resolvePackingTempCategoryKey(item?.category || item?.Category || item?.name, sku)) {
    return false;
  }

  if (sku.startsWith('BA')) return true; // bacon family SKUs
  if (/\bbacon\b/.test(blob)) return true;
  if (/\bcharcuterie\b/.test(blob)) return true;
  if (
    /\b(coppa|pancetta|lonza|bresaola|salami|guanciale|lomo|nduja|chorizo|prosciutto|speck)\b/.test(
      blob,
    )
  ) {
    return true;
  }
  return false;
}

export function orderIsBaconOrCharcuterieOnly(order) {
  const items = getOrderItems(order);
  if (!items.length) return false;
  return items.every((it) => itemLooksBaconOrCharcuterie(it));
}

/** Cold Cheetah outside banner — cargo-bike handoff cue (all caps). */
export const CHEETAH_COLD_STORAGE_BANNER =
  'CHILLED FOOD DELIVERY - HAND DIRECTLY TO SHOP MANAGER OR KITCHEN STAFF.';

/** Bacon/charcuterie-only Cheetah outside banner (all caps). */
export const CHEETAH_SHELF_STABLE_BANNER =
  'SHELF STABLE PRODUCTS, DO NOT STORE IN DIRECT SUNLIGHT';

/**
 * Outside-label storage banner (all caps).
 * - Salmon/sausage present → chilled food handoff cue (no product names in the copy)
 * - Only bacon/charcuterie → shelf-stable sunlight warning
 * - Otherwise → no banner
 * @returns {string} empty when no banner
 */
export function resolveCheetahStorageBanner(order) {
  if (orderNeedsRefrigerateSoon(order)) {
    return CHEETAH_COLD_STORAGE_BANNER;
  }
  if (orderIsBaconOrCharcuterieOnly(order)) {
    return CHEETAH_SHELF_STABLE_BANNER;
  }
  return '';
}

/** Strip any QR markup that must never appear on the inside stub. */
export function stripOrderQrFromHtml(html) {
  return String(html || '')
    .replace(/<div[^>]*class="[^"]*order-qr[^"]*"[\s\S]*?<\/div>/gi, '')
    .replace(/<div[^>]*class="[^"]*lz-qr[^"]*"[\s\S]*?<\/div>/gi, '');
}

/**
 * Outside sticky / GoDEX face markup (no stub, no product table, no prices).
 * Optional contentWidthMm / contentHeightMm reflow for GO4 3.94×5.91 stock.
 * logoAtTop: GO4 only — brand row (logo left, order QR right) is the first
 * block at the physical top of the portrait face. Default false keeps Cheetah
 * LZ17 mark column (logo + QR stacked beside ship-to meta).
 * @param {object} order
 * @param {{
 *   logoUrl?: string,
 *   qrSvg?: string,
 *   qrPayload?: string,
 *   showSignatureLines?: boolean,
 *   contentWidthMm?: number,
 *   contentHeightMm?: number,
 *   qrFloorMm?: number,
 *   logoAtTop?: boolean,
 *   brandRowClass?: string,
 * }} [options]
 */
export function buildCheetahOutsideLabelMarkup(
  order,
  {
    logoUrl,
    qrSvg,
    qrPayload,
    showSignatureLines = true,
    contentWidthMm,
    contentHeightMm,
    qrFloorMm,
    logoAtTop = false,
    brandRowClass = '',
  } = {},
) {
  const orderNumber = getOrderNumber(order);
  const shipMethod = getShippingMethod(order);
  const email = sanitize(order?.email || order?.customerEmail || order?.customer?.email || '');
  const phone = sanitize(
    order?.shipping?.phone ||
      order?.customerPhone ||
      order?.customer?.phone ||
      order?._accountDelivery?.phone ||
      '',
  );
  const { name, company, lines } = buildShipTo(order);
  const dispatch =
    toOrderDeskCheckoutDate(getOrderDispatchDate(order) || '') ||
    sanitize(getOrderDispatchDate(order) || '');
  const delivery =
    toOrderDeskCheckoutDate(getOrderDeliveryDate(order) || '') ||
    sanitize(getOrderDeliveryDate(order) || '');
  const window = formatDeliveryWindow(order);
  const notes = collectDeliveryNotes(order);
  const storageBanner = resolveCheetahStorageBanner(order);
  const printedDate = formatPackingSlipDateTime(
    order?.date_added || order?.dateAdded || order?.date_updated || Date.now(),
  );

  const metaItems = [
    true, // date
    true, // order number
    Boolean(email),
    Boolean(phone),
    Boolean(shipMethod),
    Boolean(delivery),
    Boolean(dispatch),
    Boolean(window),
  ].filter(Boolean).length;

  const addressLineCount = 1 + (name ? 1 : 0) + lines.length; // heading + name + street lines
  const notesText = notes.map(sanitize).join(' · ');
  const fit = resolveCheetahLabelFit({
    metaLineCount: metaItems,
    addressLineCount,
    notesCharCount: notesText.length,
    companyText: company,
    hasCold: Boolean(storageBanner),
    hasSigs: showSignatureLines,
    hasQr: Boolean(qrSvg),
    contentWidthMm,
    contentHeightMm,
  });
  const qrMm = Math.max(Number(fit.qrMm) || 0, Number(qrFloorMm) || 0);

  // QR only on the outside label — size follows the fit band so meta stays readable.
  const qrBlock = qrSvg
    ? `<div class="lz-qr" data-cheetah-face="label" title="${sanitize(qrPayload)}" style="--lz-qr-mm:${qrMm}mm">${qrSvg}</div>`
    : '';

  // Company only below signing lines; point size is fit-calculated (never overlaps address).
  const companyHero = company
    ? `<div class="lz-label__company" data-cheetah-company="1" style="font-size:${fit.companyPt}pt;line-height:1.12">${company}</div>`
    : '';

  const labelStyle = [
    `--lz-base:${fit.basePt}pt`,
    `--lz-meta-line:${fit.metaLineMm}mm`,
    `--lz-ship:${fit.shipPt}pt`,
    `--lz-addr-line:${fit.addrLineMm}mm`,
    `--lz-notes:${fit.notesPt}pt`,
    `--lz-notes-line:${fit.notesLineMm}mm`,
    `--lz-cold-mm:${fit.coldMm}mm`,
    `--lz-gap:${fit.gapMm}mm`,
    `--lz-qr-mm:${qrMm}mm`,
  ].join(';');

  const go4BrandRowClass = logoAtTop
    ? ` ${String(brandRowClass || 'go4-outside-brand-row').trim()}`
    : '';
  const markBlock = `
        <div class="lz-label__mark${go4BrandRowClass}"${logoAtTop ? ' data-go4-brand="top"' : ''}>
          <img
            class="lz-brand-logo"
            src="${logoUrl}"
            alt="${PACKING_SLIP_STORE_NAME}"
            width="98"
            decoding="sync"
          />
          ${qrBlock}
        </div>`;

  const topBlock = `
      <div class="lz-label__top">
        <div class="lz-label__meta">
          <ul class="lz-ul">
            <li><b>Date:</b> ${sanitize(printedDate)}</li>
            <li><b>Order Number:</b> ${sanitize(orderNumber)}</li>
            ${email ? `<li><b>Email:</b> ${email}</li>` : ''}
            ${phone ? `<li><b>Phone:</b> ${phone}</li>` : ''}
            ${shipMethod ? `<li class="lz-ship"><b>Shipping: ${sanitize(shipMethod)}</b></li>` : ''}
            ${delivery ? `<li><b>Delivery:</b> ${sanitize(delivery)}</li>` : ''}
            ${dispatch ? `<li><b>Dispatch:</b> ${sanitize(dispatch)}</li>` : ''}
            ${window ? `<li><b>Delivery window:</b> ${sanitize(window)}</li>` : ''}
          </ul>
        </div>
        ${logoAtTop ? '' : markBlock}
      </div>`;

  const restBlock = `
      ${topBlock}
      <div class="lz-addr">
        <b>Shipping Address:</b><br/>
        ${name || '&nbsp;'}<br/>
        ${lines.map((l) => `${l}<br/>`).join('')}
      </div>
      ${
        notes.length
          ? `<div class="lz-notes"><b>Notes:</b> ${notes.map(sanitize).join(' · ')}</div>`
          : ''
      }
      ${
        storageBanner
          ? `<div class="lz-cold">${sanitize(storageBanner)}</div>`
          : ''
      }
      ${
        showSignatureLines
          ? `<div class="lz-sigs">
               <div><b>Packed By:</b><br/>_______________</div>
               <div><b>Received &amp; Checked By:</b><br/>_______________</div>
             </div>`
          : ''
      }
      ${companyHero}`;

  const logoAtTopClass = logoAtTop ? ' lz-label--logo-at-top' : '';

  return `
    <div class="lz-label lz-label--${fit.key}${logoAtTopClass}" data-cheetah-face="label" data-label-fit="${fit.key}" style="${labelStyle}">
      ${logoAtTop ? markBlock : ''}
      ${logoAtTop ? `<div class="lz-label__body">${restBlock}</div>` : restBlock}
    </div>
  `;
}

const needsKgLine = (name) => {
  const s = String(name || '').toLowerCase();
  return s.includes('whole') || s.includes('unsliced');
};

/** Classic Order Desk checkout / Delivery / Notes / Dispatch lines for the in-box stub. */
function buildStubCheckoutDataLines(order) {
  const lines = [];
  const cd = order?.checkout_data || order?.checkoutData;
  if (cd && typeof cd === 'object' && !Array.isArray(cd)) {
    Object.entries(cd).forEach(([key, val]) => {
      lines.push({ key, val: val == null || val === '' ? '' : String(val) });
    });
  }

  const findLineIndex = (name) =>
    lines.findIndex((l) => String(l.key).toLowerCase() === String(name).toLowerCase());

  const upsertDateLine = (key, raw) => {
    const formatted = toOrderDeskCheckoutDate(raw) || '';
    if (!formatted) return;
    const idx = findLineIndex(key);
    if (idx >= 0) {
      if (!String(lines[idx].val || '').trim()) lines[idx] = { key: lines[idx].key, val: formatted };
      return;
    }
    lines.push({ key, val: formatted });
  };

  const delivery = getOrderDeliveryDate(order) || '';
  const dispatch = getOrderDispatchDate(order) || '';
  const slipNote = sanitize(
    order?.packingSlipNote ||
      order?.packing_slip_note ||
      order?.customer?.packingSlipNote ||
      order?.customer?.packing_slip_note ||
      order?._accountDelivery?.packingSlipNote ||
      '',
  );
  const generalNotes = sanitize(order?.notes || order?.order_notes || order?.orderNotes || '');
  const notesParts = [];
  if (slipNote) notesParts.push(slipNote);
  if (generalNotes && generalNotes !== slipNote) notesParts.push(generalNotes);
  const notesCombined = notesParts.join(' - ');

  if (!cd || lines.length === 0) {
    if (delivery) {
      lines.push({
        key: 'Delivery',
        val: toOrderDeskCheckoutDate(delivery) || delivery,
      });
    }
    lines.push({ key: 'Notes', val: notesCombined });
    if (dispatch) {
      lines.push({
        key: 'Dispatch',
        val: toOrderDeskCheckoutDate(dispatch) || dispatch,
      });
    }
    return applyCompanyCheckoutLine(lines, order);
  }

  if (findLineIndex('Notes') < 0 && notesCombined) {
    lines.push({ key: 'Notes', val: notesCombined });
  }
  upsertDateLine('Delivery', delivery);
  upsertDateLine('Dispatch', dispatch);
  return applyCompanyCheckoutLine(lines, order);
}

const estimateWrappedLines = (value, charsPerLine = 42) => {
  const length = String(value || '').trim().length;
  return length ? Math.max(1, Math.ceil(length / charsPerLine)) : 0;
};

/**
 * Variable customer metadata consumes the same rotated height as product rows.
 * Penalise the primary capacity for long checkout/address blocks so overflow
 * moves to continuation stubs instead of being hidden by the physical clip.
 */
export function estimateCheetahPrimaryCapacityPenalty(order) {
  const invoice = buildInvoiceTo(order);
  const shipping = buildShipTo(order);
  const checkoutLines = buildStubCheckoutDataLines(order);
  const fixedLines = [
    1, // date
    1, // order number
    estimateWrappedLines(order?.email || order?.customerEmail || order?.customer?.email),
    estimateWrappedLines(
      order?.shipping?.phone || order?.customerPhone || order?.customer?.phone,
    ),
    estimateWrappedLines(getShippingMethod(order)),
    ...checkoutLines.map(
      (line) =>
        estimateWrappedLines(`${line.key}: ${line.val}`, 38) || 1,
    ),
    1, // invoice heading
    estimateWrappedLines(invoice.name),
    estimateWrappedLines(invoice.company),
    estimateWrappedLines(invoice.lines.join(', '), 38),
    1, // shipping heading
    estimateWrappedLines(shipping.name),
    estimateWrappedLines(shipping.company),
    estimateWrappedLines(shipping.lines.join(', '), 38),
  ].reduce((sum, value) => sum + (Number(value) || 0), 0);

  return Math.max(0, Math.round(Math.max(0, fixedLines - 10) * 0.75 * 4) / 4);
}

function buildContinuationLabelHtml(orderNumber, pageInfo, qrSvg, qrPayload) {
  const qrBlock = qrSvg
    ? `<div class="lz-continuation-label__qr" title="${sanitize(qrPayload)}">${qrSvg}</div>`
    : '';
  return `
    <div class="lz-continuation-label" data-cheetah-face="continuation-label">
      <b>CONTINUATION SHEET</b>
      <span>BACKUP BOX QR LABEL</span>
      ${qrBlock}
      <small>Order ${sanitize(orderNumber)} · ${pageInfo.pageNumber}/${pageInfo.pageCount}</small>
    </div>
  `;
}

/**
 * Inside stub: classic Order Desk detail on the primary sheet, then intentional
 * table-only continuation stubs. Geometry / rotation stay locked on the host.
 */
function buildStubDetailHtml(
  order,
  {
    logoUrl,
    showPrices = true,
    showSignatureLines = true,
    weightsData = {},
    lineModel: providedLineModel,
    chunk: providedChunk,
    pageInfo = { pageNumber: 1, pageCount: 1 },
    continuation = false,
    density = 'normal',
  } = {},
) {
  const orderNumber = getOrderNumber(order);
  const shipMethod = getShippingMethod(order);
  const email = sanitize(order?.email || order?.customerEmail || order?.customer?.email || '');
  const phone = sanitize(
    order?.shipping?.phone ||
      order?.customerPhone ||
      order?.customer?.phone ||
      order?._accountDelivery?.phone ||
      '',
  );
  const printedDate = formatPackingSlipDateTime(
    order?.date_added || order?.dateAdded || order?.date_updated || Date.now(),
  );
  const invoice = buildInvoiceTo(order);
  const shipTo = buildShipTo(order);
  const lineModel =
    providedLineModel ||
    buildPackingSlipLineModel(order, {
      weightsData,
      showKgLines: true,
      showPrices,
      formatLineName: formatPackingSlipLineName,
    });
  const lines = providedChunk?.lines || lineModel.lines;
  const isFinal = providedChunk?.isFinal !== false;
  const dualSig = shipMethod === 'Bermondsey' || shipMethod === 'Cargo Bike';
  const footerText =
    PACKING_SLIP_ORDERDESK_DEFAULTS.footerText ||
    `Thank you for placing an order with ${PACKING_SLIP_STORE_NAME}!`;
  const checkoutLines = buildStubCheckoutDataLines(order);

  const productRows = lines
    .map((line) => {
      const variationsHtml =
        line.variations?.length
          ? `<ul class="ul-plain" style="margin-left:14px;">
               ${line.variations
                 .map((variation) =>
                   variation.key
                     ? `<li><b>${sanitize(variation.key)}:</b> ${sanitize(variation.value)}</li>`
                     : `<li>${sanitize(variation.value)}</li>`,
                 )
                 .join('')}
             </ul>`
          : '';

      const kgSpan = line.needsKg
        ? `<span class="kg_right">${
            line.weightKg != null ? `${formatPackWeightKg(line.weightKg)}kg` : '___________kg'
          }</span>`
        : '';

      const priceCells = showPrices
        ? `<td align="right">${line.unitPrice != null ? formatGbp(line.unitPrice) : ''}</td>
           <td align="right">${line.lineTotal != null ? formatGbp(line.lineTotal) : ''}</td>`
        : '';

      return `
        <tr data-line-index="${line.index}">
          <td align="center">${line.quantity}</td>
          <td>
            ${sanitize(line.name)}
            ${kgSpan}
            ${line.foodNote ? `<small class="slip-food-note">${sanitize(line.foodNote)}</small>` : ''}
            ${variationsHtml}
          </td>
          ${priceCells}
        </tr>`;
    })
    .join('');

  const priceHeader = showPrices
    ? `<th class="qty_header short_cell">Unit</th><th class="qty_header short_cell">Line</th>`
    : '';
  const totalRow = showPrices
    ? `<tr>
         <td align="center"><b>${lineModel.quantityTotal}</b></td>
         <td><b>Total</b></td>
         <td></td>
         <td align="right"><b>${
           lineModel.moneyTotal != null
             ? formatGbp(lineModel.moneyTotal)
             : sanitize(String(order?.total || order?.order_total || ''))
         }</b></td>
       </tr>`
    : `<tr><td align="center"><b>${lineModel.quantityTotal}</b></td></tr>`;

  const sigSection = !showSignatureLines
    ? ''
    : dualSig
    ? `<table class="address_table sig-grid" cellpadding="0" cellspacing="0" border="0">
         <tbody>
           <tr>
             <td><b>Packed By:</b><br/><p></p></td>
             <td><b>Received &amp; Checked By:</b><br/><p></p></td>
           </tr>
         </tbody>
       </table>`
    : `<table class="address_table sig-grid" cellpadding="0" cellspacing="0" border="0">
         <tbody>
           <tr>
             <td><b>Packed By:</b><br/><p></p></td>
           </tr>
         </tbody>
       </table>`;

  const primaryHeader = `
      <div class="slip-header">
        <div class="slip-meta">
          <ul class="ul-plain">
            <li><b>Date:</b> ${sanitize(printedDate)}</li>
            <li><b>Order Number:</b> ${sanitize(orderNumber)}</li>
            <li><b>Email:</b> ${email}</li>
            ${phone ? `<li><b>Phone:</b> ${phone}</li>` : ''}
            <div class="shippingmethod">${
              shipMethod ? `<li><b>Shipping: ${sanitize(shipMethod)}</b></li>` : ''
            }</div>
            <div class="ellipsis">
              ${checkoutLines
                .map((row) => `<li><b>${sanitize(row.key)}:</b> ${sanitize(row.val)}</li>`)
                .join('')}
            </div>
          </ul>
        </div>
        <div class="header_logo">
          <img
            class="lz-brand-logo"
            src="${logoUrl || PACKING_SLIP_DEFAULT_LOGO_URL}"
            alt="${PACKING_SLIP_STORE_NAME}"
            width="90"
            decoding="sync"
          />
        </div>
      </div>

      <table class="address_table" cellpadding="0" cellspacing="0" border="0">
        <tbody>
          <tr>
            <td>
              <b>Invoice Address:</b><br/>
              ${invoice.name || '&nbsp;'}<br/>
              ${invoice.company ? `${invoice.company}<br/>` : ''}
              ${invoice.lines.join(', ') || '&nbsp;'}<br/>
            </td>
            <td>
              <b>Shipping Address:</b><br/>
              ${shipTo.name || '&nbsp;'}<br/>
              ${shipTo.company ? `${shipTo.company}<br/>` : ''}
              ${shipTo.lines.join(', ') || '&nbsp;'}<br/>
            </td>
          </tr>
        </tbody>
      </table>`;

  const continuationHeader = `
      <div class="lz-stub-continuation">
        <div>
          <b>Order Number:</b> ${sanitize(orderNumber)}<br/>
          <b>Packing slip continuation:</b> ${pageInfo.pageNumber}/${pageInfo.pageCount}
        </div>
        <div class="header_logo">
          <img
            class="lz-brand-logo"
            src="${logoUrl || PACKING_SLIP_DEFAULT_LOGO_URL}"
            alt="${PACKING_SLIP_STORE_NAME}"
            width="90"
            decoding="sync"
          />
        </div>
      </div>`;

  return `
    <div class="lz-stub lz-stub--${density}${continuation ? ' lz-stub--continuation' : ''}" data-cheetah-face="stub" data-line-count="${lines.length}" data-page-number="${pageInfo.pageNumber}" data-page-count="${pageInfo.pageCount}">
      ${continuation ? continuationHeader : primaryHeader}
      <table class="product_table" cellpadding="2" cellspacing="0" border="0">
        <thead>
          <tr>
            <th class="qty_header short_cell">Qty</th>
            <th class="product_header">Product</th>
            ${priceHeader}
          </tr>
        </thead>
        <tbody>
          ${productRows || '<tr><td colspan="4">No line items</td></tr>'}
          ${isFinal ? totalRow : ''}
        </tbody>
      </table>
      ${
        isFinal
          ? `<p></p>
             <p></p>
             ${sigSection}
             <hr class="slip-hr">
             <hr class="slip-hr">
             <p class="receipt_footer">${sanitize(footerText)}</p>`
          : ''
      }
    </div>
  `;
}

/**
 * @param {Array|object} orders
 * @param {{
 *   logoUrl?: string,
 *   weightsData?: object,
 *   enrichAccounts?: boolean,
 *   showPrices?: boolean,
 * }} [options]
 */
export async function buildCheetahLz17PackingSlipHTML(orders, options = {}) {
  let list = Array.isArray(orders) ? orders : [orders];
  if (options.enrichAccounts !== false) {
    list = await enrichOrdersWithAccountDeliveryFields(list);
  }

  try {
    if (typeof unifiedProductService.initialize === 'function') {
      await unifiedProductService.initialize();
    }
  } catch {
    /* catalogue names fall back to line names */
  }

  const logoUrl = options.logoUrl || PACKING_SLIP_DEFAULT_LOGO_URL || logoSrc;
  const weightsData = options.weightsData || {};
  const showPrices = options.showPrices !== false;
  const showSignatureLines = options.showSignatureLines !== false;
  const paper = CHEETAH_LZ17_PAPER;
  const stub = getCheetahStubViewportMm(paper);
  const labelPad = paper.label.padMm;
  const labelTopBreathing = paper.label.contentTopBreathingMm || 0;
  const stubInnerPad = paper.stubInnerPadMm;
  const rotation = paper.stubRotationDeg;
  const perfY = paper.perforationFromTopMm;
  const labelLogoW = paper.logo?.labelWidthMm || 26;
  const stubLogoW = paper.logo?.stubWidthMm || 24;

  // QR rendered only for the outside label
  const qrMap = await renderOrderQrSvgMap(list, (o) => getOrderNumber(o), { size: 72 });

  const sheets = list.flatMap((order, orderIndex) => {
    const orderNumber = getOrderNumber(order);
    const qrSvg = orderNumber ? qrMap.get(orderNumber) || '' : '';
    const qrPayload = orderNumber ? formatOrderQrPayload(orderNumber) : '';
    const lineModel = buildPackingSlipLineModel(order, {
      weightsData,
      showKgLines: true,
      showPrices,
      formatLineName: formatPackingSlipLineName,
    });
    const pagination = paginatePackingSlipLines(lineModel.lines, {
      format: 'cheetah',
      showPrices,
      firstCapacityPenalty: estimateCheetahPrimaryCapacityPenalty(order),
    });
    const density =
      pagination.profile.key === 'compact'
        ? 'dense'
        : pagination.profile.key === 'dense'
          ? 'xdense'
          : 'normal';

    return pagination.chunks.map((chunk) => {
      const continuation = !chunk.isFirst;
      const pageInfo = {
        pageNumber: chunk.pageNumber,
        pageCount: chunk.pageCount,
      };
      const label = continuation
        ? buildContinuationLabelHtml(orderNumber, pageInfo, qrSvg, qrPayload)
        : buildCheetahOutsideLabelMarkup(order, {
            logoUrl,
            qrSvg,
            qrPayload,
            showSignatureLines,
          });
      // Dedicated stub — never embed the outside QR or storage label.
      const stubInner = stripOrderQrFromHtml(
        buildStubDetailHtml(order, {
          logoUrl,
          showPrices,
          showSignatureLines,
          weightsData,
          lineModel,
          chunk,
          pageInfo,
          continuation,
          density,
        }),
      );
      const needsBreak = orderIndex > 0 || chunk.pageIndex > 0;

      return `
<div class="lz-sheet${needsBreak ? ' lz-sheet--break' : ''}" data-cheetah-paper="lz17" data-print-order="${sanitize(orderNumber)}" data-sheet-index="${chunk.pageIndex}" data-sheet-count="${chunk.pageCount}" data-sheet-kind="${continuation ? 'continuation' : 'primary'}">
  <div class="lz-label-host">
    ${label}
  </div>
  <div class="lz-stub-host">
    <div class="lz-stub-rotate">
      <div class="lz-stub-scale">
        ${stubInner}
      </div>
    </div>
  </div>
</div>`;
    });
  });

  return `
<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8">
<title>Cheetah LZ17 Packing Sheets — ${PACKING_SLIP_STORE_NAME}</title>
<style>
@page {
  size: ${paper.pageWidthMm}mm ${paper.pageHeightMm}mm;
  margin: 0 !important;
}
html, body {
  margin: 0 !important;
  padding: 0 !important;
  width: ${paper.pageWidthMm}mm;
  background: #fff;
  color: #000;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
  font-family: Arial, Helvetica, sans-serif;
}
*, *::before, *::after { box-sizing: border-box; }

.lz-sheet {
  position: relative;
  width: ${paper.pageWidthMm}mm;
  height: ${paper.pageHeightMm}mm;
  overflow: hidden;
  margin: 0;
  padding: 0;
}
.lz-sheet--break { page-break-before: always; break-before: page; }

/* ========== OUTSIDE LABEL (sticky) ========== */
.lz-label-host {
  position: absolute;
  top: ${paper.label.topMm}mm;
  left: ${paper.label.leftMm}mm;
  width: ${paper.label.widthMm}mm;
  height: ${paper.label.heightMm}mm;
  overflow: hidden;
  padding: 0 !important;
}
.lz-label {
  width: 100%;
  height: 100%;
  /* ~5px breathing room on top + side/bottom pad (Order Desk–like spacing) */
  padding: ${labelTopBreathing + labelPad}mm ${labelPad}mm ${labelPad}mm ${labelPad}mm !important;
  font-size: var(--lz-base, 9pt);
  line-height: 1.25;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: var(--lz-gap, 1.5mm);
  overflow: hidden;
  box-sizing: border-box;
}
.lz-label > * {
  flex: 0 0 auto;
  min-width: 0;
  max-width: 100%;
}
.lz-continuation-label {
  width: 100%;
  height: 100%;
  padding: 8mm !important;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2mm;
  text-align: center;
  border: 0.5mm solid #333;
  font-size: 13pt;
  letter-spacing: 0.04em;
}
.lz-continuation-label span {
  font-size: 11pt;
  font-weight: 700;
}
.lz-continuation-label small {
  font-size: 8pt;
  letter-spacing: 0;
}
.lz-continuation-label__qr {
  width: 24mm;
  height: 24mm;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
}
.lz-continuation-label__qr svg {
  width: 24mm;
  height: 24mm;
  display: block;
}
.lz-ul {
  list-style: none;
  padding: 0;
  margin: 0;
}
.lz-ul li {
  margin: 0;
  padding: 0;
  line-height: var(--lz-meta-line, 5mm);
}
.lz-ship {
  font-size: var(--lz-ship, 11pt);
  line-height: calc(var(--lz-meta-line, 5mm) * 1.05);
  font-weight: 700;
}
.lz-label__top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 2.5mm;
}
.lz-label__meta { flex: 1 1 auto; min-width: 0; }
.lz-label__company {
  margin: 0;
  padding: 0.8mm 0 0;
  border-top: 0.3mm solid #111;
  font-weight: 800;
  letter-spacing: 0.015em;
  text-transform: uppercase;
  color: #111;
  overflow-wrap: anywhere;
  word-break: break-word;
}
.lz-label__mark {
  flex: 0 0 ${labelLogoW + 2}mm;
  flex-shrink: 0;
  width: ${labelLogoW + 2}mm;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}
/* Brand logo — locked; do not stretch, crop, filter, or density-shrink */
.lz-brand-logo {
  width: ${labelLogoW}mm !important;
  max-width: ${labelLogoW}mm !important;
  height: auto !important;
  max-height: none !important;
  object-fit: contain !important;
  object-position: center top !important;
  display: block !important;
  margin: 0 auto !important;
  border: 0 !important;
  padding: 0 !important;
  background: transparent !important;
  -webkit-print-color-adjust: exact !important;
  print-color-adjust: exact !important;
  image-rendering: auto;
}
.lz-qr {
  margin-top: 0.8mm;
  width: 100%;
  display: flex;
  justify-content: center;
}
.lz-qr svg {
  width: var(--lz-qr-mm, 16mm);
  height: var(--lz-qr-mm, 16mm);
  display: block;
}
.lz-addr {
  border-top: 0.3mm solid #333;
  padding-top: 0.8mm !important;
  overflow: hidden;
  line-height: var(--lz-addr-line, 4.5mm);
}
.lz-notes {
  border-top: 0.2mm dashed #666;
  padding-top: 0.6mm !important;
  font-size: var(--lz-notes, 8pt);
  line-height: var(--lz-notes-line, 3.5mm);
  overflow: hidden;
}
.lz-cold {
  background: #111;
  color: #fff;
  font-weight: 700;
  font-size: 6.5pt;
  min-height: calc(var(--lz-cold-mm, 8mm) - 1.5mm);
  padding: 0.8mm 1.2mm !important;
  text-align: center;
  line-height: 1.2;
  -webkit-print-color-adjust: exact !important;
  print-color-adjust: exact !important;
}
.lz-sigs {
  display: flex;
  justify-content: space-between;
  gap: 2.5mm;
  font-size: 7pt;
  border-top: 0.2mm solid #999;
  padding-top: 0.8mm !important;
  line-height: 1.2;
}

/* Label must never show product/price tables */
.lz-label-host .lz-stub-table,
.lz-label-host [data-cheetah-face="stub"] {
  display: none !important;
}

/*
 * Perforation guard: stub host starts below the physical perf line.
 * Hard clip so rotated ink cannot paint into the label half.
 */
.lz-stub-host {
  position: absolute;
  top: ${stub.topMm}mm;
  left: ${stub.leftMm}mm;
  width: ${stub.widthMm}mm;
  height: ${stub.heightMm}mm;
  overflow: hidden !important;
  padding: 0 !important;
  contain: paint;
  clip-path: inset(0);
  -webkit-clip-path: inset(0);
  /* Stub top is perforation (${perfY}mm) + clearance — never paint into the label half */
}
.lz-stub-rotate {
  position: absolute;
  top: 0;
  left: 0;
  width: ${stub.heightMm}mm;
  height: ${stub.widthMm}mm;
  -webkit-transform: rotate(${rotation}deg) translateX(-100%);
  -ms-transform: rotate(${rotation}deg) translateX(-100%);
  transform: rotate(${rotation}deg) translateX(-100%);
  -webkit-transform-origin: top left;
  -ms-transform-origin: top left;
  transform-origin: top left;
  overflow: hidden !important;
  max-width: ${stub.heightMm}mm;
  max-height: ${stub.widthMm}mm;
}
.lz-stub-scale {
  width: 100%;
  height: 100%;
  overflow: hidden !important;
  /* Extra pad on the price edge (right) — after -90° that edge meets the perforation */
  padding: ${stubInnerPad}mm ${stubInnerPad + 2.5}mm ${stubInnerPad}mm ${stubInnerPad}mm !important;
  box-sizing: border-box;
}
/* Classic Order Desk slip structure, scaled into the rotated stub host */
.lz-stub {
  width: 100%;
  height: 100%;
  font-size: 9pt;
  line-height: 18px;
  overflow: hidden;
}
.lz-stub .slip-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 3mm;
  margin: 0 0 2mm 0;
}
.lz-stub .slip-meta {
  flex: 1 1 auto;
  min-width: 0;
}
.lz-stub ul.ul-plain { list-style: none; padding: 0; margin: 0; }
.lz-stub ul.ul-plain li { margin: 0; padding: 0; }
.lz-stub .shippingmethod { font-size: 11pt; line-height: 18px; }
.lz-stub .ellipsis {
  max-width: 100%;
  overflow: hidden;
}
.lz-stub .header_logo {
  flex: 0 0 ${stubLogoW}mm;
  flex-shrink: 0;
  width: ${stubLogoW}mm;
  max-width: ${stubLogoW}mm;
  margin: 0;
  overflow: visible;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}
.lz-stub .header_logo .lz-brand-logo {
  width: ${stubLogoW}mm !important;
  max-width: ${stubLogoW}mm !important;
  height: auto !important;
  max-height: none !important;
  object-fit: contain !important;
}
.lz-stub .address_table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 2mm;
}
.lz-stub .address_table td {
  width: 50%;
  padding: 0 2mm 0 0 !important;
  vertical-align: top;
}
.lz-stub .product_table {
  margin-top: 2.5mm;
  margin-bottom: 2mm;
  width: 100%;
  border-collapse: collapse;
}
/* Normal orders: roomy product rows (the airier classic feel) */
.lz-stub .product_table th,
.lz-stub .product_table td {
  padding: 2.5mm 1.2mm !important;
  vertical-align: top;
  line-height: 1.35;
  overflow-wrap: anywhere;
  word-break: normal;
}
.lz-stub .product_table th {
  padding-top: 1mm !important;
  padding-bottom: 1.5mm !important;
}
.lz-stub .qty_header,
.lz-stub .short_cell { text-align: center; width: 14mm; }
.lz-stub .product_header { text-align: left; }
.lz-stub .product_table tr:nth-child(even) { background-color: #ededed; }
.lz-stub .product_table tr:nth-child(odd) { background-color: #ffffff; }
.lz-stub .kg_right { float: right; font-weight: bold; }
.lz-stub .sig-grid { width: 100%; border-collapse: collapse; margin-top: 2mm; }
.lz-stub .sig-grid td { width: 50%; padding: 0 !important; vertical-align: top; }
.lz-stub .slip-hr {
  width: 100%;
  border: 0;
  border-top: 1px solid #333;
  margin: 2mm 0;
}
.lz-stub .receipt_footer {
  text-align: center;
  margin: 1mm 0 0;
  font-size: 8pt;
}
.lz-stub-continuation {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 3mm;
  padding-bottom: 1.5mm !important;
  border-bottom: 0.25mm solid #333;
  font-size: 8pt;
  line-height: 1.25;
}
.lz-stub--continuation .product_table {
  margin-top: 1.5mm;
}

/* Dense product lists — keep everything below the perforation */
.lz-stub--dense {
  font-size: 8pt;
  line-height: 15px;
}
.lz-stub--dense .shippingmethod { font-size: 10pt; line-height: 16px; }
/* Logo size stays locked — density only tightens type / table padding */
.lz-stub--dense .product_table th,
.lz-stub--dense .product_table td {
  padding: 1.2mm 0.8mm !important;
  line-height: 1.25;
}
.lz-stub--xdense {
  font-size: 7pt;
  line-height: 13px;
}
.lz-stub--xdense .shippingmethod { font-size: 9pt; line-height: 14px; }
.lz-stub--xdense .product_table th,
.lz-stub--xdense .product_table td {
  padding: 0.5mm 0.6mm !important;
  line-height: 1.15;
}
.lz-stub--xdense .receipt_footer { font-size: 6.5pt; }

/* Inside stub must never show the outside QR */
.lz-stub-host .lz-qr,
.lz-stub-host .order-qr,
.lz-stub-host [title^="LSC:ORDER:"],
.lz-stub-scale .lz-qr,
.lz-stub-scale .order-qr {
  display: none !important;
  visibility: hidden !important;
  width: 0 !important;
  height: 0 !important;
  overflow: hidden !important;
}
</style>
</head>
<body>
<!-- content split: label=${CHEETAH_LZ17_CONTENT_SPLIT.labelIncludes.join(',')} | stub=${CHEETAH_LZ17_CONTENT_SPLIT.stubIncludes.join(',')} -->
${sheets.join('\n')}
</body>
</html>
  `.trim();
}

export { CHEETAH_LZ17_CONTENT_SPLIT };
export default buildCheetahLz17PackingSlipHTML;
